import React, { useRef } from "react";
import { Layer, Rect } from "react-konva";

import { getResizeHandlerPositionsForZone, isPointWithinRadiusOf } from "../helpers";
import { Dispatch, ResizeHandlerPosition, State } from "../StateManager/state";
import { Zone } from "../types";
import { Offset, Point, Scale, Size } from "./types";
import { RESIZE_RADIUS } from "./ZoneResizeControl";

type Props = Readonly<{
  autoShrink: boolean;
  disableActions: boolean;
  dispatch: Dispatch;
  offset: Offset;
  scale: Scale;
  size: Size;
  state: State;
}>;

const convertToImageCoords = (stage: any, offset: Offset, scale: Scale): Point => {
  const cursor = stage.getPointerPosition();
  return {
    x: (cursor.x - offset.x) / scale.x,
    y: (cursor.y - offset.y) / scale.y,
  };
};

const getResizeHandlerPosition = (
  activeZones: ReadonlyArray<number>,
  zones: ReadonlyArray<Zone>,
  pointerPosition: Point,
  scale: Scale,
  offset: Offset,
): ResizeHandlerPosition | null => {
  if (activeZones.length !== 1) {
    return null;
  }
  const hoveredResizePosition = getResizeHandlerPositionsForZone(zones[activeZones[0]])
    .map(p => ({ x: p.x * scale.x + offset.x, y: p.y * scale.y + offset.y }))
    .findIndex(
      isPointWithinRadiusOf({ x: pointerPosition.x, y: pointerPosition.y }, RESIZE_RADIUS),
    );
  switch (hoveredResizePosition) {
    case 0:
      return ResizeHandlerPosition.TOP;
    case 1:
      return ResizeHandlerPosition.RIGHT;
    case 2:
      return ResizeHandlerPosition.BOTTOM;
    case 3:
      return ResizeHandlerPosition.LEFT;
    default:
      return null;
  }
};

export const MouseHandler = ({
  autoShrink,
  disableActions,
  dispatch,
  offset,
  scale,
  size,
  state,
}: Props) => {
  if (isNaN(offset.x) || isNaN(offset.y) || isNaN(size.height) || isNaN(size.width)) {
    return <Layer />;
  }
  const stageRef = useRef<any>(null);
  const { activePage, pages } = state;
  const { activeZones, zones } = pages[activePage];
  return (
    <Layer ref={stageRef}>
      <Rect
        {...size}
        {...offset}
        onMouseDown={e => {
          if (disableActions) {
            return;
          }
          const stage = e.target.getStage();
          if (stage) {
            const location = convertToImageCoords(stage, offset, scale);
            const pointerPosition = stage.getPointerPosition();
            if (pointerPosition) {
              dispatch({
                ctrlKey: !!e.evt.ctrlKey,
                location,
                resizeHandlerPosition: getResizeHandlerPosition(
                  activeZones,
                  zones,
                  pointerPosition,
                  scale,
                  offset,
                ),
                shiftKey: !!e.evt.shiftKey,
                type: "MOUSE DOWN",
              });
            }
          }
        }}
        onMouseLeave={() => {
          if (disableActions) {
            return;
          }
          dispatch({ type: "MOUSE LEAVE" });
        }}
        onMouseMove={e => {
          const location = convertToImageCoords(e.target.getStage(), offset, scale);
          dispatch({
            imageSize: { height: size.height / scale.y, width: size.width / scale.x },
            location,
            shift: e.evt.shiftKey,
            type: "MOUSE MOVE",
          });
        }}
        onMouseUp={e => {
          if (disableActions) {
            return;
          }
          const location = convertToImageCoords(e.target.getStage(), offset, scale);
          dispatch({
            ctrl: e.evt.ctrlKey,
            shift: e.evt.shiftKey,
            autoShrink,
            imageSize: { height: size.height / scale.y, width: size.width / scale.x },
            location,
            type: "MOUSE UP",
          });
        }}
      />
    </Layer>
  );
};
